import React from 'react';
import Icon from '../../../components/AppIcon';

const SiteComparison = ({ auditData }) => {
  const siteData = [
    {
      site: 'Plant 1 - Main Facility',
      completionRate: 78,
      averageScore: 87,
      participants: 45,
      completed: 35,
      rank: 2,
      strengths: ['General Safety', 'Fire Safety'],
      improvements: ['Equipment Safety']
    },
    {
      site: 'Plant 2 - North Wing',
      completionRate: 92,
      averageScore: 91,
      participants: 30,
      completed: 28,
      rank: 1,
      strengths: ['All Categories', 'Excellent Compliance'],
      improvements: []
    },
    {
      site: 'Plant 3 - South Wing',
      completionRate: 100,
      averageScore: 94,
      participants: 20,
      completed: 20,
      rank: 1,
      strengths: ['Perfect Completion', 'High Scores'],
      improvements: []
    },
    {
      site: 'Warehouse 1',
      completionRate: 0,
      averageScore: 0,
      participants: 25,
      completed: 0,
      rank: 4,
      strengths: [],
      improvements: ['Not Yet Started']
    }
  ];

  const categoryComparison = [
    {
      category: 'General Safety',
      plant1: 92,
      plant2: 95,
      plant3: 96,
      warehouse1: 0
    },
    {
      category: 'Fire Safety',
      plant1: 85,
      plant2: 90,
      plant3: 92,
      warehouse1: 0
    },
    {
      category: 'Emergency Procedures',
      plant1: 81,
      plant2: 88,
      plant3: 91,
      warehouse1: 0
    },
    {
      category: 'PPE Compliance',
      plant1: 88,
      plant2: 92,
      plant3: 95,
      warehouse1: 0
    },
    {
      category: 'Equipment Safety',
      plant1: 79,
      plant2: 85,
      plant3: 90,
      warehouse1: 0
    }
  ];

  const getRankBadge = (rank) => {
    const badges = {
      1: { color: 'bg-yellow-500', icon: 'Award', label: '1st' },
      2: { color: 'bg-gray-400', icon: 'Medal', label: '2nd' },
      3: { color: 'bg-orange-600', icon: 'Medal', label: '3rd' },
      4: { color: 'bg-gray-300', icon: 'Circle', label: '4th' }
    };
    return badges?.[rank] || badges?.[4];
  };

  return (
    <div className="space-y-6">
      {/* Site Performance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {siteData?.map((site, index) => {
          const badge = getRankBadge(site?.rank);
          return (
            <div
              key={index}
              className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-base font-semibold text-foreground">{site?.site}</h3>
                    <div className={`w-8 h-8 rounded-full ${badge?.color} flex items-center justify-center`}>
                      <Icon name={badge?.icon} size={16} color="white" />
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span>{site?.completed}/{site?.participants} completed</span>
                  </div>
                </div>
              </div>
              {/* Progress Metrics */}
              <div className="space-y-3 mb-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-muted-foreground">Completion Rate</span>
                    <span className="text-sm font-bold text-foreground">{site?.completionRate}%</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className="bg-blue-500 h-2 rounded-full transition-all"
                      style={{ width: `${site?.completionRate}%` }}
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-muted-foreground">Average Score</span>
                    <span className="text-sm font-bold text-foreground">{site?.averageScore}%</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className="bg-green-500 h-2 rounded-full transition-all"
                      style={{ width: `${site?.averageScore}%` }}
                    />
                  </div>
                </div>
              </div>
              {/* Strengths & Improvements */}
              {site?.strengths?.length > 0 && (
                <div className="mb-3">
                  <div className="text-xs font-medium text-muted-foreground mb-1">Strengths</div>
                  <div className="flex flex-wrap gap-1">
                    {site?.strengths?.map((strength, idx) => (
                      <span key={idx} className="px-2 py-1 bg-green-500/10 text-green-700 text-xs rounded">
                        {strength}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {site?.improvements?.length > 0 && (
                <div>
                  <div className="text-xs font-medium text-muted-foreground mb-1">Needs Improvement</div>
                  <div className="flex flex-wrap gap-1">
                    {site?.improvements?.map((improvement, idx) => (
                      <span key={idx} className="px-2 py-1 bg-orange-500/10 text-orange-700 text-xs rounded">
                        {improvement}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {/* Category Comparison Table */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Category-wise Site Comparison</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Category</th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-foreground">Plant 1</th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-foreground">Plant 2</th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-foreground">Plant 3</th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-foreground">Warehouse 1</th>
              </tr>
            </thead>
            <tbody>
              {categoryComparison?.map((category, index) => {
                const scores = [category?.plant1, category?.plant2, category?.plant3, category?.warehouse1];
                const maxScore = Math.max(...scores?.filter(s => s > 0));

                return (
                  <tr key={index} className="border-b border-border last:border-0">
                    <td className="py-3 px-4 text-sm font-medium text-foreground">{category?.category}</td>
                    {['plant1', 'plant2', 'plant3', 'warehouse1']?.map((plant, idx) => {
                      const score = category?.[plant];
                      const isMax = score === maxScore && score > 0;
                      
                      return (
                        <td key={idx} className="text-center py-3 px-4">
                          <span className={`text-sm font-medium ${isMax ? 'text-green-600 font-bold' : 'text-foreground'}`}>
                            {score > 0 ? `${score}%` : '-'}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {/* Best Practices */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Identified Best Practices</h3>
        <div className="space-y-3">
          <div className="flex items-start gap-3 p-4 bg-green-500/10 rounded-lg">
            <Icon name="CheckCircle" size={20} className="text-green-500 mt-0.5" />
            <div>
              <div className="text-sm font-medium text-foreground mb-1">Plant 3 - Perfect Completion</div>
              <div className="text-xs text-muted-foreground">
                Plant 3 achieved 100% completion rate with highest average scores. Consider sharing their engagement strategies with other sites.
              </div>
            </div>
          </div>

          <div className="flex items-start gap-3 p-4 bg-blue-500/10 rounded-lg">
            <Icon name="Award" size={20} className="text-blue-500 mt-0.5" />
            <div>
              <div className="text-sm font-medium text-foreground mb-1">Plant 2 - Excellence in All Categories</div>
              <div className="text-xs text-muted-foreground">
                Plant 2 demonstrated strong performance across all safety categories. Their training program could serve as a model.
              </div>
            </div>
          </div>

          <div className="flex items-start gap-3 p-4 bg-orange-500/10 rounded-lg">
            <Icon name="AlertCircle" size={20} className="text-orange-500 mt-0.5" />
            <div>
              <div className="text-sm font-medium text-foreground mb-1">Warehouse 1 - Requires Immediate Attention</div>
              <div className="text-xs text-muted-foreground">
                No responses received yet. Recommend immediate follow-up and investigation into barriers to participation.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SiteComparison;